/**
 * A button that opens a short menu under it (redesign §24): the role cell's
 * Member / Admin / Make owner (§41.2) and a row's « More actions » (§41.3).
 *
 * The menu closes on a pick, a click elsewhere or Escape. A held item stays in
 * the list at 40 % with its reason as a tooltip, never removed, so the list
 * keeps its shape from one row to the next.
 *
 * From the keyboard (§24, rule 31): opening puts the focus on the first item,
 * ↑ ↓ Home End move it through the items (held ones included: they say why),
 * Escape and a pick give it back to the button, Tab closes the menu and goes on.
 */
import { useRef, useState } from 'react';
import type React from 'react';
import { color, radius, shadow } from '../theme/tokens';
import { ChevronDown } from './icons';
import { useDismiss } from './Shell';
import { useTooltip } from './Tooltip';
import { useMotionMount } from '../lib/presence';

export interface MenuItem {
  label: string;
  onPick: () => void;
  /** A menuitemradio, ticked when it is the current value. */
  checked?: boolean;
  danger?: boolean;
  /** Why the item is held; it stays listed and focusable. */
  reason?: string;
  /** A rule above this item. */
  separated?: boolean;
}

export function MenuButton({
  label,
  items,
  align = 'left',
  icon = false
}: {
  /** The button's text, or its accessible name when `icon` draws « ⋯ ». */
  label: string;
  items: MenuItem[];
  align?: 'left' | 'right';
  icon?: boolean;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useDismiss(open, () => setOpen(false));
  const button = useRef<HTMLButtonElement>(null);
  const close = (): void => {
    setOpen(false);
    button.current?.focus();
  };
  // The items, in order, to move the focus through.
  const rows = (): HTMLButtonElement[] => [...(ref.current?.querySelectorAll<HTMLButtonElement>('[role^="menuitem"]') ?? [])];
  const move = (event: React.KeyboardEvent): void => {
    if (event.key === 'Escape' && open) {
      // Before the page's own Escape (useDismiss listens on the document).
      event.stopPropagation();
      close();
      return;
    }
    if (event.key === 'Tab' && open) {
      setOpen(false);
      return;
    }
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    if (!open) {
      setOpen(true);
      window.requestAnimationFrame(() => rows()[event.key === 'ArrowUp' || event.key === 'End' ? rows().length - 1 : 0]?.focus());
      return;
    }
    const all = rows();
    const at = all.indexOf(document.activeElement as HTMLButtonElement);
    const next =
      event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? all.length - 1
          : (at + (event.key === 'ArrowDown' ? 1 : -1) + all.length) % all.length;
    all[next]?.focus();
  };
  // Entrance o, y −4→0, s .98→1 (d 1 r .3); exit o, s .98 in 120 ms (§35).
  const list = useMotionMount<HTMLDivElement>({ o: 0, y: -4, s: 0.98 }, { d: 1, r: 0.3 }, { o: 0, s: 0.98 });
  const name = useTooltip<HTMLButtonElement>(icon && !open ? label : undefined);
  return (
    <div ref={ref} onKeyDown={move} style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={icon ? label : undefined}
        {...name.trigger}
        ref={(el) => {
          button.current = el;
          name.trigger.ref(el);
        }}
        onClick={(event) => {
          // A keyboard press (detail 0) lands on the first item; a click leaves the focus on the button.
          const keyboard = event.detail === 0;
          setOpen((was) => !was);
          if (keyboard && !open) window.requestAnimationFrame(() => rows()[0]?.focus());
        }}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          height: 28,
          padding: icon ? '0 6px' : '0 8px',
          margin: icon ? 0 : '0 0 0 -8px',
          border: 0,
          borderRadius: radius.md,
          background: open ? 'rgba(0,0,0,.055)' : 'transparent',
          fontFamily: 'inherit',
          fontSize: 13.5,
          color: color.ink,
          cursor: 'pointer'
        }}
      >
        {icon ? <span aria-hidden="true" style={{ fontSize: 16, lineHeight: 1, color: color.inkMuted }}>⋯</span> : label}
        {!icon && (
          <span style={{ display: 'flex', color: color.inkFaint }}>
            <ChevronDown open={open} />
          </span>
        )}
      </button>
      {name.tip}
      {open && (
        <div
          ref={list}
          role="menu"
          style={{
            transformOrigin: align === 'left' ? 'top left' : 'top right',
            position: 'absolute',
            top: 32,
            [align]: 0,
            zIndex: 30,
            minWidth: icon ? 220 : 200,
            padding: 6,
            background: color.surface,
            borderRadius: radius.xl,
            boxShadow: shadow.menu
          }}
        >
          {items.map((item) => (
            <div key={item.label}>
              {item.separated && <div role="separator" style={{ height: 1, margin: '6px 4px', background: color.line }} />}
              <MenuRow
                item={item}
                onPick={() => {
                  close();
                  item.onPick();
                }}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** One line of the menu; a held one says why in a tooltip (§24, §26). */
function MenuRow({ item, onPick }: { item: MenuItem; onPick: () => void }): React.JSX.Element {
  const { trigger, tip } = useTooltip<HTMLButtonElement>(item.reason);
  return (
    <>
      <button
        type="button"
        role={item.checked === undefined ? 'menuitem' : 'menuitemradio'}
        tabIndex={-1}
        aria-checked={item.checked}
        aria-disabled={item.reason ? true : undefined}
        {...trigger}
        onClick={() => {
          if (!item.reason) onPick();
        }}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          width: '100%',
          height: 32,
          padding: '0 10px',
          border: 0,
          borderRadius: radius.md,
          background: 'transparent',
          textAlign: 'left',
          fontFamily: 'inherit',
          fontSize: 13.5,
          color: item.danger ? color.red : color.ink,
          opacity: item.reason ? 0.4 : 1,
          cursor: item.reason ? 'default' : 'pointer'
        }}
        className="menu-item"
      >
        <span aria-hidden="true" style={{ width: 14, flex: 'none', color: color.accentText }}>{item.checked ? '✓' : ''}</span>
        {item.label}
      </button>
      {tip}
    </>
  );
}
